import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, query } from "../../lib/api";
import { formatRelative } from "../../lib/format";
import type { KYCQueueItem, ListResponse } from "../../lib/types";
import {
  EmptyState,
  PageHeader,
  Pagination,
  Sheet,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

const FILTERS = [
  { value: "", label: "Awaiting a decision" },
  { value: "submitted", label: "Submitted" },
  { value: "in_review", label: "Under review" },
  { value: "info_requested", label: "Information requested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export function KycQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [offset, setOffset] = useState(0);

  const queue = useQuery({
    queryKey: ["kyc-queue", status, applied, offset],
    queryFn: () =>
      api.get<ListResponse<KYCQueueItem>>(
        `/api/admin/kyc${query({ status, search: applied, limit: PAGE_SIZE, offset })}`,
      ),
    refetchInterval: 30_000,
  });

  const rows = queue.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Verification queue"
        description="Firms waiting to be approved for live payments. Oldest first, so nothing is left behind."
      />

      <div className="settle settle-1 mb-4 flex flex-wrap items-center gap-2">
        {/* The queue is ordered oldest-first so nothing is starved, which means a
            specific firm is not necessarily on the first page. An operator following
            up an enquiry needs to find it directly. */}
        <form
          className="mr-1 min-w-[220px] flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(search.trim());
            setOffset(0);
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a firm by name or email…"
            aria-label="Search the verification queue"
            className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-1.5 text-[13px] placeholder:text-ink-faint focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
          />
        </form>

        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            onClick={() => {
              setStatus(filter.value);
              setOffset(0);
            }}
            className={[
              "rounded-full px-3 py-1 text-[12.5px] transition-colors duration-150",
              status === filter.value
                ? "bg-ink text-paper"
                : "bg-paper-raised text-ink-muted ring-1 ring-inset ring-rule-strong hover:text-ink",
            ].join(" ")}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <Sheet className="settle settle-2 overflow-hidden">
        {queue.isLoading ? (
          <Skeleton />
        ) : rows.length === 0 ? (
          <EmptyState
            title={applied ? "No firms match" : "Nothing waiting"}
            description={
              applied
                ? "Try a different name, or clear the search."
                : "Every submitted application has been dealt with."
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Client firm</Th>
                  <Th>Status</Th>
                  <Th align="right">Documents</Th>
                  <Th align="right">People</Th>
                  <Th align="right">Waiting</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <Tr
                    key={item.application_id}
                    onClick={() => navigate(`/admin/verification/${item.application_id}`)}
                  >
                    <Td>
                      <span className="text-[13.5px] text-ink">{item.tenant_name}</span>
                      <span className="mt-0.5 block font-mono text-[11px] text-ink-faint">
                        {item.tenant_id}
                      </span>
                    </Td>
                    <Td>
                      <StatusPill status={item.status} />
                    </Td>
                    <Td align="right" className="tabular">
                      {item.document_count}
                    </Td>
                    <Td align="right" className="tabular">
                      {item.director_count}
                    </Td>
                    <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                      {item.submitted_at ? formatRelative(item.submitted_at) : "Not submitted"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={queue.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>
    </>
  );
}
